// src/pages/DebtStudio/components/Portfolios/Analysis/index.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import SectionCard from "components/SectionCard";
import {
  ALL_DIMENSION_VALUES,
  DimensionValueCompositeSelect,
  selectedDimensionValueIds,
} from "components/DimensionValueFilter";
import { supabase } from "services/supabaseClient";
import { useProfile } from "context/ProfileContext";
import dayjs from "lib/dayjs";

import PrincipalAllocation from "./PrincipalAllocation";
import CallOptionality from "./CallOptionality";

import { buildPortfolioDetails } from "pages/DebtStudio/lib/buildPortfolioDetails";

/* ------------------------------------------------------------------
 * Component
 * ------------------------------------------------------------------ */

const MEMBER_SELECT = `
  id,
  ds_deal_id,
  include,
  sort_order,
  created_at,
  ds_deals:ds_deal_id (
    *,
    issuers:issuer_id (
      legal_name
    )
  )
`;

function n(x) {
  const v = Number(x);
  return Number.isFinite(v) ? v : 0;
}

function fmtYears(x) {
  const v = Number(x);
  if (!Number.isFinite(v)) return "—";
  return `${v.toFixed(2)} yrs`;
}

// Coupon already in percent units (e.g. 5 means 5.000%)
function fmtPct3(x) {
  const v = Number(x);
  if (!Number.isFinite(v)) return "—";
  return `${v.toFixed(3)}%`;
}

function fmtPct1FromDecimal(x) {
  const v = Number(x);
  if (!Number.isFinite(v)) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function fmtPeriodEndMmDd(dateStr) {
  if (!dateStr) return "(not set)";
  const d = dayjs(dateStr);
  if (!d.isValid()) return dateStr;
  return d.format("MM-DD");
}

function yearFracAct365(fromDate, toDate) {
  const a = dayjs(fromDate);
  const b = dayjs(toDate);
  if (!a.isValid() || !b.isValid()) return null;
  const days = b.diff(a, "day");
  return days / 365;
}

function computeWalYearsFromCouponSchedules(issues, asOfDate) {
  const asOf = dayjs(asOfDate);
  if (!asOf.isValid()) return null;

  let sumP = 0;
  let sumPT = 0;

  for (const it of issues || []) {
    for (const r of it?.couponSchedule || []) {
      const d = dayjs(r?.date);
      if (!d.isValid()) continue;

      if (!d.isAfter(asOf, "day")) continue;

      const p = n(r?.principal);
      if (p <= 0) continue;

      const t = yearFracAct365(asOf, d);
      if (t == null) continue;

      sumP += p;
      sumPT += p * t;
    }
  }

  return sumP > 0 ? sumPT / sumP : null;
}

function computeWacPctFromStructureRows(issues) {
  let sumPar = 0;
  let sumParCpn = 0;

  for (const it of issues || []) {
    const rows = Array.isArray(it?.deal?.structure)
      ? it.deal.structure
      : (() => {
          try {
            return typeof it?.deal?.structure === "string"
              ? JSON.parse(it.deal.structure)
              : [];
          } catch {
            return [];
          }
        })();

    for (const r of rows || []) {
      if (!r || typeof r !== "object") continue;

      const par = n(r?.principal);
      if (par <= 0) continue;

      const cpnPct = n(r?.coupon);
      if (!Number.isFinite(cpnPct) || cpnPct <= 0) continue;

      sumPar += par;
      sumParCpn += par * cpnPct;
    }
  }

  return sumPar > 0 ? sumParCpn / sumPar : null;
}

function computeCallablePctCurrentPeriod(callableVsNoncallableOutstandingFY) {
  const rows = callableVsNoncallableOutstandingFY || [];
  const current = rows.length ? rows[0] : null;
  if (!current) return { callablePct: null, callableAsOf: null };

  const total = n(current.total);
  const callable = n(current.callable);

  return {
    callablePct: total > 0 ? callable / total : null,
    callableAsOf: current.date || null,
  };
}

function KpiTile({ label, value, description }) {
  return (
    <div
      className="rounded-lg border border-border bg-app p-3"
      title={description || ""}
    >
      <div className="text-xs text-muted">{label}</div>
      <div className="mt-1 text-lg font-semibold text-app leading-tight">
        {value}
      </div>
    </div>
  );
}

function PortfolioReportActions({ onExportPdf, isPdfGenerating, filterItems, issueFilter, onIssueFilterChange }) {
  return (
    <div className="mb-3 flex flex-wrap items-end justify-end gap-2">
      <DimensionValueCompositeSelect
        label="Issues"
        items={filterItems}
        selection={issueFilter}
        onChange={onIssueFilterChange}
      />
      {typeof onExportPdf === "function" ? <button
          type="button"
          className="btn btn-primary shrink-0 text-sm"
          onClick={onExportPdf}
          disabled={isPdfGenerating}
          title="Export full DebtStudio - Portfolio Report to PDF"
        >
          {isPdfGenerating ? "Generating PDF..." : "Export PDF"}
        </button> : null}
    </div>
  );
}

export default function PortfoliosAnalysis({
  portfolio,
  onExportPdf = null,
  isPdfGenerating = false,
  issueFilter = ALL_DIMENSION_VALUES,
  onIssueFilterChange = null,
}) {
  const { selectedOrg } = useProfile();

  const portfolioId = portfolio?.id || null;
  const orgId = selectedOrg?.id || null;

  const effectivePeriodEnd = useMemo(() => {
    const persisted =
      typeof portfolio?.period_end === "string" ? portfolio.period_end : "";
    return persisted || "";
  }, [portfolio?.period_end]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [members, setMembers] = useState([]);

  const fetchMembers = useCallback(async () => {
    if (!portfolioId || !orgId) return;

    setLoading(true);
    setError("");

    try {
      const { data, error: err } = await supabase
        .from("ds_portfolio_issues")
        .select(MEMBER_SELECT)
        .eq("org_id", orgId)
        .eq("portfolio_id", portfolioId)
        .order("sort_order", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: true });

      if (err) throw err;

      setMembers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("❌ [PortfoliosAnalysis] fetch members failed:", e);
      setError(e?.message || "Failed to load portfolio issues.");
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [portfolioId, orgId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const hasAnyIssues = useMemo(() => {
    return (members || []).some((m) => m?.include !== false);
  }, [members]);

  const includedMembers = useMemo(() => {
    return (members || []).filter((m) => m?.include !== false);
  }, [members]);

  const portfolioDetails = useMemo(() => buildPortfolioDetails({
    members: includedMembers,
    fiscalYearEndOverride: effectivePeriodEnd || null,
    asOfDate: null,
  }), [effectivePeriodEnd, includedMembers]);
  const issueFilterItems = useMemo(
    () => (portfolioDetails?.issues || []).map((issue) => ({
      id: issue.issueId,
      label: issue.label,
    })),
    [portfolioDetails?.issues]
  );
  const selectedIssueIds = useMemo(
    () => selectedDimensionValueIds(issueFilter, issueFilterItems),
    [issueFilter, issueFilterItems]
  );
  const filteredMembers = useMemo(
    () => includedMembers.filter((member) => {
      const deal = member?.ds_deals || member?.ds_deals_row || member?.deal;
      return deal?.id && selectedIssueIds.has(deal.id);
    }),
    [includedMembers, selectedIssueIds]
  );
  const filteredPortfolioDetails = useMemo(() => buildPortfolioDetails({
    members: filteredMembers,
    fiscalYearEndOverride: effectivePeriodEnd || null,
    asOfDate: null,
  }), [effectivePeriodEnd, filteredMembers]);

  const kpis = useMemo(() => {
    if (!filteredMembers.length) return null;
    const issues = filteredPortfolioDetails?.issues || [];
    const asOf = dayjs().startOf("day");

    const walYears = computeWalYearsFromCouponSchedules(issues, asOf);
    const wacPct = computeWacPctFromStructureRows(issues);

    const callKpis = computeCallablePctCurrentPeriod(
      filteredPortfolioDetails?.callableVsNoncallableOutstandingFY || []
    );

    return {
      walYears,
      wacPct,
      asOf: asOf.format("YYYY-MM-DD"),
      ...callKpis,
    };
  }, [filteredMembers.length, filteredPortfolioDetails]);

  return (
    <div className="space-y-4">
      <PortfolioReportActions
        onExportPdf={onExportPdf}
        isPdfGenerating={isPdfGenerating}
        filterItems={issueFilterItems}
        issueFilter={issueFilter}
        onIssueFilterChange={onIssueFilterChange}
      />

      <SectionCard title="Debt Portfolio">
        <div className="text-sm">
          <span className="text-muted">Annual Period End: </span>
          <span className="text-app">
            {fmtPeriodEndMmDd(effectivePeriodEnd)}
          </span>
        </div>

        {error ? (
          <div className="mt-3 rounded-md border border-border bg-app p-3 text-sm">
            <div className="text-red-600 font-medium">Issue</div>
            <div className="text-muted mt-1">{error}</div>
          </div>
        ) : null}

        {!loading && !error && !hasAnyIssues ? (
          <div className="mt-4 text-muted">
            No portfolio issues included. Add issues on the Main tab.
          </div>
        ) : null}

        {!loading && !error && hasAnyIssues && kpis ? (
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <KpiTile
              label={`WAL (as of ${kpis.asOf})`}
              value={kpis.walYears == null ? "—" : fmtYears(kpis.walYears)}
              description="Weighted average number of years until principal is repaid, based on future principal cashflows."
            />

            <KpiTile
              label="Weighted Avg Coupon"
              value={kpis.wacPct == null ? "—" : fmtPct3(kpis.wacPct)}
              description="Par-weighted average stated coupon across all maturities in the portfolio."
            />

            <KpiTile
              label={`Callable %${
                kpis.callableAsOf
                  ? ` (current period to ${kpis.callableAsOf})`
                  : ""
              }`}
              value={
                kpis.callablePct == null
                  ? "—"
                  : fmtPct1FromDecimal(kpis.callablePct)
              }
              description="Percentage of outstanding par that is callable within the current annual period bucket."
            />
          </div>
        ) : null}

        {!loading && !error && hasAnyIssues && !kpis ? (
          <div className="mt-4 text-muted">
            No issues selected. Use the Issues filter to calculate portfolio metrics.
          </div>
        ) : null}
      </SectionCard>

      <PrincipalAllocation
        members={filteredMembers}
        fiscalYearEndOverride={effectivePeriodEnd || null}
      />

      <CallOptionality
        members={filteredMembers}
        fiscalYearEndOverride={effectivePeriodEnd || null}
      />
    </div>
  );
}
